/**
 * Adapters to convert backend message DTOs to frontend WhatsAppMessage types
 */
import {
  MessageResponse,
  MessageTypeEnum,
  SystemEventEnum,
} from '../types/backend';
import { WhatsAppMessage } from '../../types/chat';

/**
 * Convert backend MessageResponse to frontend WhatsAppMessage
 */
export const adaptMessageToWhatsApp = (
  message: MessageResponse,
  currentUserId: number
): WhatsAppMessage => {
  // Optimistic messages are already in WhatsApp format, return as-is
  if ((message as any).__optimistic) {
    return message as unknown as WhatsAppMessage;
  }

  const isSystemMessage = message.type === MessageTypeEnum.SYSTEM;
  const isOutgoing = message['sender-user-id'] === currentUserId;

  // Use delivery status from BE for outgoing messages
  const status: WhatsAppMessage['status'] = isOutgoing
    ? (message['delivery-status'] as WhatsAppMessage['status'])
    : undefined;

  // Determine 'from' field:
  // - Internal user messages have sender-user-id
  // - External WhatsApp messages have whatsapp-sender-phone (no sender-user-id)
  // - True system messages have neither
  const fromValue = message['sender-user-id']
    ? String(message['sender-user-id'])
    : message['whatsapp-sender-phone']
      ? message['whatsapp-sender-phone']
      : 'system';

  const adapted: WhatsAppMessage = {
    id: String(message.id),
    from: fromValue,
    to: String(message['channel-id']),
    type: determineMessageType(message),
    timestamp: new Date(message['date-added']).getTime(),
    conversationId: String(message['channel-id']),
    direction: isOutgoing ? 'outgoing' : 'incoming',
    status,
    isEdited: message['is-edited'],
    isDeleted: message['is-deleted'],
  };

  // Add text content
  if (message.content) {
    adapted.text = {
      body: message.content,
    };
  }

  // Add sender name for group messages (internal user or external WhatsApp sender)
  if (message.sender && !isOutgoing) {
    adapted.senderName = `${message.sender['first-name']} ${message.sender['last-name']}`;
  } else if (message['whatsapp-sender-name'] && !isOutgoing) {
    adapted.senderName = message['whatsapp-sender-name'];
  } else if (message['whatsapp-sender-phone'] && !isOutgoing) {
    adapted.senderName = message['whatsapp-sender-phone'];
  }

  // Add reply-to metadata
  if (message['reply-to-message'] && message['reply-to-message-id']) {
    const reply = message['reply-to-message'];
    adapted.replyTo = {
      messageId: String(message['reply-to-message-id']),
      senderName: reply.sender
        ? `${reply.sender['first-name']} ${reply.sender['last-name']}`
        : 'Unknown',
      text: reply.content || '',
      type: 'text', // Simplified for now
    };
  }

  // Add attachments
  if (message.attachments && message.attachments.length > 0) {
    // Convert all attachments to media format
    adapted.attachments = message.attachments
      .filter((att) => att.storedFile)
      .map((attachment) => ({
        filename: attachment.storedFile!.name,
        mimeType: attachment.storedFile!.contentType,
        url: `/api/messaging/channels/${message['channel-id']}/files/${attachment.storedFile!.accessKey}`,
        id: String(attachment.storedFile!.id),
        thumbnailUrl: attachment.storedFile!.thumbnail
          ? `/api/messaging/channels/${message['channel-id']}/files/${attachment.storedFile!.thumbnail.accessKey}`
          : undefined,
        thumbnailWidth: attachment.storedFile!.thumbnail?.width,
        thumbnailHeight: attachment.storedFile!.thumbnail?.height,
      }));

    // Keep first attachment as media for backward compatibility
    if (adapted.attachments.length > 0) {
      adapted.media = adapted.attachments[0];
    }
  }

  // Handle system messages
  if (isSystemMessage && message['system-event']) {
    adapted.text = {
      body: formatSystemMessage(
        message['system-event'],
        message['system-event-data'],
        currentUserId
      ),
    };
  }

  // Preserve optimistic flag for pending messages
  if ((message as any).__optimistic) {
    (adapted as any).__optimistic = true;
  }

  return adapted;
};

/**
 * Determine frontend message type from backend message
 */
const determineMessageType = (
  message: MessageResponse
): WhatsAppMessage['type'] => {
  if (
    message.attachments &&
    message.attachments.length > 0 &&
    message.attachments[0].storedFile
  ) {
    const contentType = message.attachments[0].storedFile.contentType;

    // If multiple attachments, default to document type
    // Individual files will show their correct types in the UI
    if (message.attachments.length > 1) return 'document';

    if (contentType.startsWith('image/')) return 'image';
    if (contentType.startsWith('video/')) return 'video';
    if (contentType.startsWith('audio/')) return 'audio';
    return 'document';
  }

  return 'text';
};

/**
 * Format system event messages for display
 */
const formatSystemMessage = (
  event: SystemEventEnum,
  data: any,
  currentUserId: number
): string => {
  // Helper to format actor name (use "You" if it's the current user)
  const formatActor = (actorId?: number, actorName?: string): string => {
    if (actorId === currentUserId) return 'You';
    return actorName || 'System';
  };

  // Helper to format target name (use "you" if it's the current user)
  const formatTarget = (targetId?: number, targetName?: string): string => {
    if (targetId === currentUserId) return 'you';
    return targetName || 'the system';
  };

  switch (event) {
    case SystemEventEnum.MEMBER_ADDED:
      const addedActor = formatActor(data.addedByUserId, data.addedByUserName);
      const addedTarget = formatTarget(data.userId, data.userName);
      return `${addedActor} added ${addedTarget} to the conversation`;

    case SystemEventEnum.MEMBER_REMOVED:
      const removedActor = formatActor(
        data.removedByUserId,
        data.removedByUserName
      );
      const removedTarget = formatTarget(data.userId, data.userName);
      return `${removedActor} removed ${removedTarget} from the conversation`;

    case SystemEventEnum.MEMBER_LEFT:
      const leftUser = formatTarget(data.userId, data.userName);
      return `${leftUser.charAt(0).toUpperCase() + leftUser.slice(1)} left the conversation`;

    case SystemEventEnum.CHANNEL_CREATED:
      const creator = formatActor(data.createdByUserId, data.createdByUserName);
      return `${creator} started this conversation`;

    case SystemEventEnum.CHANNEL_RENAMED:
      const renamer = formatActor(data.userId, data.userName);
      return `${renamer} renamed the channel to "${data.newName || 'Unnamed'}"`;

    case SystemEventEnum.CHANNEL_ARCHIVED:
      const archiver = formatActor(data.userId, data.userName);
      return `${archiver} archived the channel`;

    case SystemEventEnum.CHANNEL_UNARCHIVED:
      const unarchiver = formatActor(data.userId, data.userName);
      return `${unarchiver} unarchived the channel`;

    default:
      return ' ';
  }
};

/**
 * Batch convert messages
 */
export const adaptMessages = (
  messages: MessageResponse[],
  currentUserId: number
): WhatsAppMessage[] => {
  return messages.map((msg) => adaptMessageToWhatsApp(msg, currentUserId));
};
